// Step 1: Set Fundamentals - Filter Format, Technology, Chemistry

import { h } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { 
  workflowAllCells,
  fundamentalsCells,
  selectedFormats,
  selectedTechnologies,
  selectedChemistries
} from '../../state/benchmarkingWorkflowState.js'
import { IconFilter, IconCheck, IconBox, IconBattery, IconFlask } from '../shared/Icons.jsx'

export default function Step1Fundamentals() {
  const [availableFormats, setAvailableFormats] = useState([])
  const [availableTechnologies, setAvailableTechnologies] = useState([])
  const [availableChemistries, setAvailableChemistries] = useState([])
  
  // Extract unique values from cells
  useEffect(() => {
    const cells = workflowAllCells.value || []
    
    // Extract unique formats
    const formats = new Set()
    cells.forEach(cell => {
      const format = cell.format || cell.mechanical?.format || cell.mechanical?.format_group
      if (format) formats.add(format)
    })
    setAvailableFormats(Array.from(formats).sort())
    
    // Extract unique technologies
    const techs = new Set()
    cells.forEach(cell => {
      const tech = cell.technology || cell.general?.technology
      if (tech) techs.add(tech)
    })
    setAvailableTechnologies(Array.from(techs).sort())
    
    // Extract unique chemistries
    const chems = new Set()
    cells.forEach(cell => {
      const chem = cell.main_chemistry || cell.chemical?.main_chemistry
      if (chem) chems.add(chem)
    })
    setAvailableChemistries(Array.from(chems).sort())
  }, [workflowAllCells.value])
  
  const toggleFormat = (format) => {
    const current = selectedFormats.value
    if (current.includes(format)) {
      selectedFormats.value = current.filter(f => f !== format)
    } else {
      selectedFormats.value = [...current, format]
    }
  }
  
  const toggleTechnology = (tech) => {
    const current = selectedTechnologies.value
    if (current.includes(tech)) {
      selectedTechnologies.value = current.filter(t => t !== tech)
    } else {
      selectedTechnologies.value = [...current, tech]
    }
  }
  
  const toggleChemistry = (chem) => {
    const current = selectedChemistries.value
    if (current.includes(chem)) {
      selectedChemistries.value = current.filter(c => c !== chem)
    } else {
      selectedChemistries.value = [...current, chem]
    }
  }
  
  // Count cells per filter option with dynamic filtering
  const getFormatCount = (format) => {
    const cells = workflowAllCells.value || []
    return cells.filter(cell => {
      const cellFormat = cell.format || cell.mechanical?.format || cell.mechanical?.format_group
      if (cellFormat !== format) return false
      
      // Apply other active filters
      if (selectedTechnologies.value.length > 0) {
        const cellTech = cell.technology || cell.general?.technology
        if (!selectedTechnologies.value.includes(cellTech)) return false
      }
      if (selectedChemistries.value.length > 0) {
        const cellChem = cell.main_chemistry || cell.chemical?.main_chemistry
        if (!selectedChemistries.value.includes(cellChem)) return false
      }
      return true
    }).length
  }
  
  const getTechnologyCount = (tech) => {
    const cells = workflowAllCells.value || []
    return cells.filter(cell => {
      const cellTech = cell.technology || cell.general?.technology
      if (cellTech !== tech) return false
      
      // Apply other active filters
      if (selectedFormats.value.length > 0) {
        const cellFormat = cell.format || cell.mechanical?.format || cell.mechanical?.format_group
        if (!selectedFormats.value.includes(cellFormat)) return false
      }
      if (selectedChemistries.value.length > 0) {
        const cellChem = cell.main_chemistry || cell.chemical?.main_chemistry
        if (!selectedChemistries.value.includes(cellChem)) return false
      }
      return true
    }).length
  }
  
  const getChemistryCount = (chem) => {
    const cells = workflowAllCells.value || []
    return cells.filter(cell => {
      const cellChem = cell.main_chemistry || cell.chemical?.main_chemistry
      if (cellChem !== chem) return false
      
      // Apply other active filters
      if (selectedFormats.value.length > 0) {
        const cellFormat = cell.format || cell.mechanical?.format || cell.mechanical?.format_group
        if (!selectedFormats.value.includes(cellFormat)) return false
      }
      if (selectedTechnologies.value.length > 0) {
        const cellTech = cell.technology || cell.general?.technology
        if (!selectedTechnologies.value.includes(cellTech)) return false
      }
      return true
    }).length
  }
  
  const totalCells = workflowAllCells.value?.length || 0
  const filteredCount = fundamentalsCells.value?.length || 0
  const isFiltered = selectedFormats.value.length > 0 || selectedTechnologies.value.length > 0 || selectedChemistries.value.length > 0
  
  return (
    <div className="step-fundamentals">
      <div className="step-fundamentals-card" data-tour="benchmarking-step1">
        <div className="step-header">
          <div className="step-title">
            <IconFilter size={24} />
            <h2>Set Fundamentals</h2>
          </div>
          <p className="step-description">
            Filter cells by basic characteristics. Select one or more options from each category.
          </p>
        </div>
        
        {/* Cell Count Feedback */}
        <div className="fundamentals-feedback">
        <div className="feedback-box">
          <div className="feedback-label">Matching Cells</div>
          <div className="feedback-count">
            <span className="count-number">{filteredCount}</span>
            <span className="count-total">/ {totalCells}</span>
          </div>
          {isFiltered && (
            <div className="feedback-status">
              <IconCheck size={16} />
              <span>Filters active</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Three Filters Side by Side */}
      <div className="fundamentals-filters">
        {/* Format Filter */}
        <div className="filter-column" data-tour="benchmarking-format">
          <div className="filter-column-header">
            <div className="filter-column-title">
              <IconBox size={18} />
              <h3>Format</h3>
            </div>
            <span className="filter-count">{selectedFormats.value.length} selected</span>
          </div>
          <div className="filter-options">
            {availableFormats.map(format => {
              const count = getFormatCount(format)
              const isDisabled = count === 0
              return (
                <label key={format} className={`filter-option ${isDisabled ? 'filter-option--disabled' : ''}`}>
                  <input 
                    type="checkbox"
                    checked={selectedFormats.value.includes(format)}
                    onChange={() => toggleFormat(format)}
                    disabled={isDisabled}
                  />
                  <span className="filter-option-label">{format}</span>
                  <span className="filter-option-count">{count}</span>
                </label>
              )
            })}
            {availableFormats.length === 0 && (
              <div className="filter-empty">No formats available</div>
            )}
          </div>
        </div>
        
        {/* Technology Filter */}
        <div className="filter-column" data-tour="benchmarking-technology">
          <div className="filter-column-header">
            <div className="filter-column-title">
              <IconBattery size={18} />
              <h3>Technology</h3>
            </div>
            <span className="filter-count">{selectedTechnologies.value.length} selected</span>
          </div>
          <div className="filter-options">
            {availableTechnologies.map(tech => {
              const count = getTechnologyCount(tech)
              const isDisabled = count === 0
              return (
                <label key={tech} className={`filter-option ${isDisabled ? 'filter-option--disabled' : ''}`}>
                  <input 
                    type="checkbox"
                    checked={selectedTechnologies.value.includes(tech)}
                    onChange={() => toggleTechnology(tech)}
                    disabled={isDisabled}
                  />
                  <span className="filter-option-label">{tech}</span>
                  <span className="filter-option-count">{count}</span>
                </label>
              )
            })}
            {availableTechnologies.length === 0 && (
              <div className="filter-empty">No technologies available</div>
            )}
          </div>
        </div>
        
        {/* Chemistry Filter */}
        <div className="filter-column" data-tour="benchmarking-chemistry">
          <div className="filter-column-header">
            <div className="filter-column-title">
              <IconFlask size={18} />
              <h3>Chemistry</h3>
            </div>
            <span className="filter-count">{selectedChemistries.value.length} selected</span>
          </div>
          <div className="filter-options">
            {availableChemistries.map(chem => {
              const count = getChemistryCount(chem)
              const isDisabled = count === 0
              return (
                <label key={chem} className={`filter-option ${isDisabled ? 'filter-option--disabled' : ''}`}>
                  <input 
                    type="checkbox"
                    checked={selectedChemistries.value.includes(chem)}
                    onChange={() => toggleChemistry(chem)}
                    disabled={isDisabled}
                  />
                  <span className="filter-option-label">{chem}</span>
                  <span className="filter-option-count">{count}</span>
                </label>
              )
            })}
            {availableChemistries.length === 0 && (
              <div className="filter-empty">No chemistries available</div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}
